import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { UserApi } from '../../store/user/user.api';

type PushConfigResponse = {
  data?: {
    public_key?: string;
  };
};

type PushSubscriptionJson = {
  endpoint?: string;
  keys?: {
    auth?: string;
    p256dh?: string;
  };
};

@Injectable({
  providedIn: 'root',
})
export class WebPushService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);
  private readonly userApi = inject(UserApi);

  private readonly registrationInFlight = signal(false);
  private lastUserId: string | null = null;

  async ensureSubscribed(userId: string | null | undefined): Promise<void> {
    const normalizedUserId = (userId ?? '').trim();
    if (!normalizedUserId || this.registrationInFlight() || !isPlatformBrowser(this.platformId)) {
      return;
    }

    if (!this.isSupported()) {
      return;
    }

    if (this.lastUserId === normalizedUserId) {
      return;
    }

    this.registrationInFlight.set(true);
    try {
      const permission = await this.ensurePermission();
      if (permission !== 'granted') {
        return;
      }

      const publicKey = await this.fetchPublicKey();
      if (!publicKey) {
        return;
      }

      const applicationServerKey = this.urlBase64ToUint8Array(publicKey);
      const subscription = await this.subscribeWithRetry(applicationServerKey);

      const payload = subscription.toJSON() as PushSubscriptionJson;
      if (!payload.endpoint || !payload.keys?.auth || !payload.keys?.p256dh) {
        return;
      }

      await firstValueFrom(this.userApi.saveWebPushSubscription({
        endpoint: payload.endpoint,
        keys: {
          auth: payload.keys.auth,
          p256dh: payload.keys.p256dh,
        },
      }));

      this.lastUserId = normalizedUserId;
    } catch (error) {
      console.error('Unable to register web push subscription', error);
    } finally {
      this.registrationInFlight.set(false);
    }
  }

  private isSupported(): boolean {
    return typeof window !== 'undefined'
      && 'Notification' in window
      && 'serviceWorker' in navigator
      && 'PushManager' in window
      && window.isSecureContext
      && !!this.document.defaultView;
  }

  private async ensurePermission(): Promise<NotificationPermission> {
    if (Notification.permission === 'granted') {
      return 'granted';
    }
    if (Notification.permission === 'denied') {
      return 'denied';
    }
    return Notification.requestPermission();
  }

  private async fetchPublicKey(): Promise<string | null> {
    try {
      const response = await firstValueFrom(this.userApi.getWebPushConfig()) as PushConfigResponse | undefined;
      return response?.data?.public_key?.trim() || null;
    } catch (error) {
      console.error('Unable to fetch web push config', error);
      return null;
    }
  }

  private urlBase64ToUint8Array(value: string): Uint8Array {
    const padding = '='.repeat((4 - value.length % 4) % 4);
    const base64 = (value + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = atob(base64);
    const output = new Uint8Array(raw.length);
    for (let index = 0; index < raw.length; index += 1) {
      output[index] = raw.charCodeAt(index);
    }
    return output;
  }

  private async subscribeWithRetry(applicationServerKey: Uint8Array): Promise<PushSubscription> {
    const registration = await this.registerAndWaitForActiveServiceWorker();

    try {
      return await this.getOrCreateSubscription(registration, applicationServerKey);
    } catch (error) {
      await registration.unregister().catch(() => {});
      const retriedRegistration = await this.registerAndWaitForActiveServiceWorker();
      return this.getOrCreateSubscription(retriedRegistration, applicationServerKey);
    }
  }

  private async registerAndWaitForActiveServiceWorker(): Promise<ServiceWorkerRegistration> {
    await navigator.serviceWorker.register('/push-sw.js');
    return navigator.serviceWorker.ready;
  }

  private async getOrCreateSubscription(
    registration: ServiceWorkerRegistration,
    applicationServerKey: Uint8Array,
  ): Promise<PushSubscription> {
    const existing = await registration.pushManager.getSubscription();
    if (existing) {
      return existing;
    }

    return registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });
  }
}
